/**
 * Public support page — anyone visiting bidorah.com can file a ticket.
 *
 * No auth required, but if the visitor is signed in we stamp their
 * user_id on the backend so admins see the link. We deliberately keep
 * this page calm and trust-building — clean form, clear "we read every
 * one" copy, a visible support@bidorah.com address so power users can
 * email directly once Cloudflare Email Routing is active.
 */
import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LifebuoyIcon, EnvelopeSimpleIcon, PaperPlaneTiltIcon, CheckCircleIcon } from "@phosphor-icons/react";

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/support/tickets", {
        ...form,
        context: {
          page: typeof window !== "undefined" ? window.location.href : null,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        },
      });
      setSent(r.data?.ticket_id || "ok");
      toast.success("Ticket received. We'll reply to your email shortly.");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16" data-testid="support-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-2xl bg-[#1D78AB]/10 border border-[#1D78AB]/30 grid place-items-center">
            <LifebuoyIcon size={22} weight="fill" className="text-[#1D78AB]" />
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
            How can we help?
          </h1>
        </div>
        <p className="text-zinc-600 leading-relaxed">
          We read every message. Most tickets get a real human reply within a
          few hours — escrow disputes within 24 hours.
        </p>
        <p className="text-zinc-600 leading-relaxed mt-2">
          Prefer email? Reach us directly at{" "}
          <a
            href="mailto:support@bidorah.com"
            className="font-bold text-[#1D78AB] hover:underline inline-flex items-center gap-1"
            data-testid="support-email-link"
          >
            <EnvelopeSimpleIcon size={14} weight="fill" />
            support@bidorah.com
          </a>
        </p>

        {sent ? (
          <div
            className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 p-6 flex items-start gap-3"
            data-testid="support-success"
          >
            <CheckCircleIcon size={28} weight="fill" className="text-emerald-600 shrink-0" />
            <div>
              <p className="font-display font-black text-lg text-emerald-900">
                Got it — ticket #{sent} is in our queue.
              </p>
              <p className="text-sm text-emerald-800 mt-1">
                We'll reply to <strong>{form.email}</strong>. Please check your
                spam folder if you don't hear back within a day.
              </p>
              <button
                type="button"
                className="mt-4 text-sm font-bold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                onClick={() => {
                  setSent(null);
                  setForm({ name: "", email: "", subject: "", message: "" });
                }}
              >
                File another
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-8 rounded-2xl border border-zinc-200 bg-white p-6 sm:p-7 space-y-4"
            data-testid="support-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                  Your name
                </Label>
                <Input
                  id="name"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 placeholder:text-zinc-500"
                  data-testid="support-name"
                  placeholder="Pat Stevens"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                  Reply-to email
                </Label>
                <Input
                  id="email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 placeholder:text-zinc-500"
                  data-testid="support-email"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject" className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                Subject
              </Label>
              <Input
                id="subject"
                required
                maxLength={200}
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-1.5 placeholder:text-zinc-500"
                data-testid="support-subject"
                placeholder="Order #1234 — refund question"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                Tell us what's going on
              </Label>
              <Textarea
                id="message"
                required
                rows={8}
                maxLength={8000}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1.5 placeholder:text-zinc-500"
                data-testid="support-message"
                placeholder="The more detail you can share, the faster we can fix it."
              />
              <p className="text-[10px] text-zinc-400 mt-1 text-right">
                {form.message.length} / 8000
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-[11px] text-zinc-500">
                By submitting you agree to our{" "}
                <a href="/terms" className="underline hover:text-zinc-800">Terms</a> &amp;{" "}
                <a href="/privacy" className="underline hover:text-zinc-800">Privacy Policy</a>.
              </p>
              <Button
                type="submit"
                disabled={busy}
                className="gap-2"
                data-testid="support-submit"
              >
                <PaperPlaneTiltIcon size={16} weight="fill" />
                {busy ? "Sending…" : "Send ticket"}
              </Button>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </>
  );
}
